import React, { ReactNode } from 'react'
import { ProductSkusTableProps, skuTableWidths } from './ProductSkusTable.types'
import styles from './ProductSkusTable.module.scss'
// import cl from 'classnames'
import { inject } from 'mobx-react'
import { PRODUCT_SKU_STORE, PRODUCT_STORE } from 'store'
import { LightTag, PaginationButtons, TableBodyContainer, TableBodyLine, TableCellProps, TableColHeaderProps, TableContainer, TableHeader, TableSliderFilter, Text1 } from 'components/shared'
import { SkuCell } from './SkuCell'
import { capitalize, formatPrice, getTableHeight, SortDirection } from 'utilities'
import { Attribute, AttributeTypes, SoldProductSkuDTO } from 'models'
import { SkuTableSortProps } from 'store/ProductSkuStore/ProductSkuStore.types'
import { observer } from 'mobx-react-lite'
import tableBodyContainerStyle from '../../../../components/shared/TableBodyContainer/TableBodyContainer.module.scss'

const MIN_CELL_WIDTH = 140

const ProductSkusTableComponent = (props: ProductSkusTableProps) => {
  const {
    [PRODUCT_SKU_STORE]: {
      // getSearchedProductSkus,
      // productId
      skus,
      attributeLabels,
      attributesCount,
      getSortDirection,
      isColumnMarked,
      setSortDirection,
      paginationInfo,
      onPageSelect,
      skuSearchProps,
      onSearchPropsChange,
      priceRange,
      onPriceFilterChange,
      totalSkuCount,
      stockRange,
      onStockFilterChange,
      soldRange,
      onSoldFilterChange,
      revenueRange,
      onRevenueFilterChange,
      getSelectOptionsByAttrLabel,
      getAttrFilterValueByAttrLabel,
      onAttributeFilterSelect
    }
  } = props

  const handleAttributeFilterChange = (attrLabel: string) => (attrId: number) => {
    onAttributeFilterSelect(attrLabel, attrId)
  }

  const attributeHeaderCols: TableColHeaderProps[] = attributeLabels
    .map((attrLabel, index) => {
      const isFirst = index === 0

      return ({
        label: attrLabel,
        preset: 'grouped',
        groupLabel: isFirst ? 'Variations' : undefined,
        groupWidth: isFirst ? MIN_CELL_WIDTH * attributesCount : undefined,
        widthPx: MIN_CELL_WIDTH,
        selectProps: {
          options: getSelectOptionsByAttrLabel(attrLabel),
          value: getAttrFilterValueByAttrLabel(attrLabel),
          onChange: handleAttributeFilterChange(attrLabel),
          noneLabel: 'All'
        }
      })
    })

  const getAttributeSkuCells = (sku: SoldProductSkuDTO): TableCellProps[] => {
    const { attributes = [] } = sku

    const getAttrTagChildren = (attr: Attribute): ReactNode => {
      const isColor = attr.type === AttributeTypes.COLOR
      if (isColor) {
        return (
          <>
            <div
              className={styles.colorCircle}
              style={{ backgroundColor: attr.value }}
            />
            {capitalize(attr.name.trim().toLowerCase())}
          </>
        )
      }

      const isSize = attr.type === AttributeTypes.SIZE
      if (isSize) {
        return attr.name.trim().toUpperCase()
      }

      return capitalize(attr.name.trim().toLowerCase())
    }

    return attributeLabels.map(attrLabel => {
      const attribute = attributes.find(attr => attr.attributeName === attrLabel)
      const children: ReactNode = attribute
        ? <LightTag>{getAttrTagChildren(attribute)}</LightTag>
        : <Text1 className={styles.notAvailable}>N/A</Text1>

      return ({
        widthPx: MIN_CELL_WIDTH,
        children
      })
    })
  }

  const handleSetSortDirection = (sortProp: SkuTableSortProps) =>
    (direction: SortDirection) => {
      setSortDirection(sortProp, direction)
    }

  const tableHeight = getTableHeight(totalSkuCount + 2)

  const scrollbarSpacing = 4.8

  return (
    <TableContainer
      noBoxShadow
      style={{ marginTop: 16, minHeight: 250, maxHeight: tableHeight, height: tableHeight, overflowX: 'auto' }}
      className={styles.thinScroll}
    >
      <TableHeader
        tableColumns={[
          {
            label: 'SKU (Stock Keeping Unit)',
            className: styles.skuColHeader,
            widthPx: skuTableWidths.sku,
            sortDirection: getSortDirection(SkuTableSortProps.SKU),
            setSortDirection: handleSetSortDirection(SkuTableSortProps.SKU),
            isColumnMarked: isColumnMarked(SkuTableSortProps.SKU),
            inputProps: {
              placeholder: 'Search...',
              value: skuSearchProps.sku,
              onChange: sku => onSearchPropsChange({ sku })
            }
          },
          ...attributeHeaderCols,
          {
            label: 'Price',
            widthPx: skuTableWidths.price + scrollbarSpacing,
            sortDirection: getSortDirection(SkuTableSortProps.PRICE),
            setSortDirection: handleSetSortDirection(SkuTableSortProps.PRICE),
            isColumnMarked: isColumnMarked(SkuTableSortProps.PRICE),
            filterComponent: (
              <TableSliderFilter
                label='Price'
                min={10} // Fix the issue slider not working properly default min value is 10
                max={priceRange.max}
                onChange={onPriceFilterChange}
              />
            )
          },
          {
            label: 'Items in stock',
            widthPx: skuTableWidths.stock,
            sortDirection: getSortDirection(SkuTableSortProps.STOCK),
            setSortDirection: handleSetSortDirection(SkuTableSortProps.STOCK),
            isColumnMarked: isColumnMarked(SkuTableSortProps.STOCK),
            filterComponent: (
              <TableSliderFilter
                label='Items'
                min={1} // Fix the issue slider not working properly default min value is 1
                max={stockRange.max}
                onChange={onStockFilterChange}
              />
            )
          },
          {
            label: 'Sold',
            widthPx: skuTableWidths.sold,
            filterComponent: (
              <TableSliderFilter
                label='Items'
                min={soldRange.min}
                max={soldRange.max}
                onChange={onSoldFilterChange}
              />
            )
          },
          {
            label: 'Revenue',
            widthPx: skuTableWidths.revenue,
            filterComponent: (
              <TableSliderFilter
                style={{ maxWidth: MIN_CELL_WIDTH - 24 }}
                label='Items'
                min={revenueRange.min}
                max={revenueRange.max}
                onChange={onRevenueFilterChange}
              />
            )
          }
        ]}
      />
      <TableBodyContainer className={tableBodyContainerStyle.tableBodyContainer}>
        {skus.map(sku => {
          return (
            <TableBodyLine
              key={sku.id}
              className={styles.tableLine}
              cells={[
                {
                  widthPx: skuTableWidths.sku,
                  children: (
                    <SkuCell sku={sku} />
                  ),
                  isColumnMarked: isColumnMarked(SkuTableSortProps.SKU)
                },
                ...getAttributeSkuCells(sku),
                {
                  widthPx: skuTableWidths.price,
                  children: formatPrice(sku.price),
                  isColumnMarked: isColumnMarked(SkuTableSortProps.PRICE)
                },
                {
                  widthPx: skuTableWidths.stock,
                  children: sku.stock,
                  isColumnMarked: isColumnMarked(SkuTableSortProps.STOCK)
                },
                {
                  widthPx: skuTableWidths.sold,
                  children: sku.soldCount
                },
                {
                  widthPx: skuTableWidths.revenue,
                  // style: { flex: 1, maxWidth: 'unset', minWidth: MIN_CELL_WIDTH, paddingRight: 12 },
                  children: formatPrice(sku.total)
                }
              ]}
            />
          )
        })}
      </TableBodyContainer>
      <PaginationButtons
        {...paginationInfo}
        onPageSelect={onPageSelect}
      />
    </TableContainer>
  )
}

const ProductSkusTable = inject(
  PRODUCT_STORE, PRODUCT_SKU_STORE
)(observer(ProductSkusTableComponent))
export { ProductSkusTable }
